import { useCallback, useEffect, useState } from 'react'

import { useLocation } from '@tanstack/react-router'

import { CaptureBox } from '../screens/memo/capture'
import { Modal } from '../components/modal'
import { useProjects } from './queries'
import { t } from '../copy'
import { T } from '../copy/text'

/**
 * 按 N 弹出来的「随手记一条」（设计稿 S13/S14/M03）。
 *
 * ## 作用域跟着路由走（S14）
 *
 * 在 `/projects/:id` 里按 N，默认存当前项目；别处按 N 存杂项。
 * 作用域行在输入框正上方一直可见——「东西存到哪儿，必须一眼看见」。
 *
 * ## 保存成功之前这个框不会关（S13 的硬规矩）
 *
 * 旧版先关框再发请求，「不打断」了，但失败的那次内容就没了下文。
 * 现在框开着等结果：成功才关；失败内容留在框里（红条 + 重试），
 * 草稿同时在按作用域存的本机那份里。
 */
export function QuickMemo(props?: { open?: boolean; onClose?: () => void }) {
  // 受控（备忘录页底部输入栏拉起）与自持（全局 N 键）两种用法：
  // 传了 open 就听外层的；没传就自己管——壳里那个全局挂载不用改。
  const controlled = props?.open !== undefined
  const [selfOpen, setSelfOpen] = useState(false)
  const open = controlled ? props!.open! : selfOpen
  const setOpen = controlled
    ? (next: boolean) => {
        if (!next) props!.onClose?.()
      }
    : setSelfOpen
  const location = useLocation()
  // /projects/:id 下（含子路由）默认存当前项目
  const match = location.pathname.match(/^\/projects\/([0-9a-f-]{36})/)
  const projectId = match?.[1]
  const projects = useProjects()
  const projectName = projectId
    ? (projects.data?.items.find((p) => p.id === projectId)?.name ?? '')
    : undefined

  useQuickMemoHotkey(
    useCallback(() => {
      setOpen(true)
    }, []),
  )

  const inProject = !!projectId

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      // 项目名没到之前用通用标题——空变量渲染层直接抛，
      // 快记框崩了会拖垮整条路由（项目页实测栽过这一下）
      title={
        inProject && projectName ? t('memo.quick.title.project', { project: projectName }) : t('memo.quick.title')
      }
      description={inProject ? t('memo.quick.desc.project') : t('memo.quick.desc.global')}
      // 手机端（M03）：从底部升起的面板——顶部圆角、贴底、留出键盘之上的空间
      className="border border-m-line2 bg-m-bg1! text-m-t1 shadow-[var(--m-pop)] max-[700px]:top-auto! max-[700px]:bottom-0! max-[700px]:translate-x-[-50%]! max-[700px]:translate-y-0! max-[700px]:w-[100vw]! max-[700px]:rounded-b-none! max-[700px]:rounded-t-[12px]! max-[700px]:pb-[16px]"
    >
      <div data-testid="quick-memo">
        <CaptureBox
          variant="floating"
          scope={{ projectId, projectName }}
          onSaved={() => setOpen(false)}
        />
        <p className="mt-[10px] mb-0 flex flex-wrap items-center gap-x-[8px] text-[12.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <T id="memo.quick.note" />
          <span aria-hidden>·</span>
          <T id="memo.quick.foot" />
        </p>
      </div>
    </Modal>
  )
}

/**
 * 「在任何页面按一下 N」——难的是**它得知道什么时候不该响应**。
 *
 * 一个绑在 `document` 上的单字母快捷键，默认行为是**抢走用户正在打的字**。
 * 让位规则：
 *
 * | 让位给谁 | 判据 |
 * |---|---|
 * | 正在输入 | `input` / `textarea` / `select`，或任何 `contenteditable` |
 * | 组合键 | 带 Ctrl / Cmd / Alt 的一律不接（Cmd+N 是浏览器开新窗口） |
 * | 输入法 | `isComposing`——中文输入时按键会先进候选框 |
 * | 已经开着的弹窗 | Radix 开着 Dialog 时把 `body` 标成 `data-scroll-locked` |
 *
 * **`isComposing` 那一条容易漏，而漏了的后果只有中文用户碰得到**：
 * 拼音输入法里 `n` 是个再常见不过的声母，不判它的话「你」「那」「能」
 * 这些字每打一个都会弹一次框。这个产品的用户全程在打中文。
 */
function useQuickMemoHotkey(onFire: () => void) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'n' && event.key !== 'N') return
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (event.isComposing || event.keyCode === 229) return
      if (isTyping(event.target)) return
      // 已经有弹窗开着的时候不再叠一个。Radix 打开 Dialog 时会锁 body 滚动，
      // 用它当判据比自己维护一个「现在有几个弹窗」的计数可靠——
      // 那种计数会在某个组件忘了减一的那天永久卡住
      if (document.body.hasAttribute('data-scroll-locked')) return

      event.preventDefault()
      onFire()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onFire])
}

/** 焦点在能打字的东西上吗。 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}
